<template>
  <div v-if="items.length" :class="['cg', { 'cg--block': blocking, 'cg--folded': folded && !blocking }]">
    <div v-if="blocking" class="cg__veil" aria-hidden="true"></div>
    <section class="cg__panel" role="alertdialog" aria-live="assertive" :aria-label="title">
      <header class="cg__head">
        <span class="cg__siren" aria-hidden="true">{{ blocking ? '⛔' : '⚠️' }}</span>
        <div class="cg__titles">
          <h2 class="cg__title">{{ title }}</h2>
          <p class="cg__lead">
            Позвоните водителю и отметьте, что с машиной. От ответа зависят 700 ₽ за каждый час простоя: без ответа акт заказчику не уйдёт.
          </p>
        </div>
        <button v-if="!blocking" class="cg__fold" type="button" @click="folded = !folded">
          {{ folded ? 'Развернуть' : 'Свернуть' }}
        </button>
      </header>

      <div v-show="!folded || blocking" class="cg__body">
        <div v-if="groups.morning.length" class="cg__group">
          <h3 class="cg__group-title">☎️ Обзвон водителей — что с машиной?</h3>
          <CheckRow v-for="item in groups.morning" :key="item.id" :item="item" :busy="busyId === item.id"
                    @answer="send" />
        </div>
        <div v-if="groups.downtime.length" class="cg__group">
          <h3 class="cg__group-title">⏱ Подтвердите простой</h3>
          <CheckRow v-for="item in groups.downtime" :key="item.id" :item="item" :busy="busyId === item.id"
                    @answer="send" />
        </div>
        <p v-if="error" class="cg__error">{{ error }}</p>
        <p v-if="!blockEnabled && blockWarning" class="cg__warn">⚠️ {{ blockWarning }} Сейчас тестовый период — блокировки нет.</p>
        <p v-if="testMode" class="cg__test">Тестовый режим: письма заказчику уходят только после подтверждения руководителя.</p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { answerCheck, fetchMyChecks } from '@/backend/confirmApi'
import { STAGE_TEXT, groupChecks, headline, isCall, leftText, normalizeTime } from '@/components/system/confirmLogic.mjs'

const POLL_MS = 60 * 1000
const data = ref(null)
const folded = ref(false)
const busyId = ref(null)
const error = ref('')
const route = useRoute()
let timer = null

const items = computed(() => data.value?.items || [])
const blocking = computed(() => Boolean(data.value?.blocking))
const testMode = computed(() => Boolean(data.value?.test_mode))
const blockEnabled = computed(() => Boolean(data.value?.block_enabled))
const blockWarning = computed(() => data.value?.block_warning || '')
const groups = computed(() => groupChecks(items.value))
const title = computed(() => headline(data.value))

async function load() {
  try {
    data.value = await fetchMyChecks()
  } catch (e) {
    // Нет связи или нет прав — окно просто не показываем, работу не блокируем.
    if (!data.value) data.value = { items: [], blocking: false }
  }
}

async function send(item, answer, leftAt, comment) {
  error.value = ''
  busyId.value = item.id
  try {
    await answerCheck(item.id, { answer, leftAt, comment })
    await load()
  } catch (e) {
    error.value = e?.response?.data?.detail || e?.message || 'Не удалось сохранить ответ'
  } finally {
    busyId.value = null
  }
}

watch(blocking, (value) => { if (value) folded.value = false })
watch(() => route.fullPath, load)

onMounted(() => {
  load()
  timer = setInterval(load, POLL_MS)
})
onBeforeUnmount(() => clearInterval(timer))

const FALLBACK_ANSWERS = [
  { key: 'standing', label: 'Стоит' },
  { key: 'left', label: 'Уехала', needs_time: true },
  { key: 'unreachable', label: 'Не дозвонился' },
]
const TONE = { standing: 'stand', loaded_downtime: 'stand', not_loaded: 'stand', unloaded_late: 'warn', unreachable: 'ghost' }

const CheckRow = defineComponent({
  name: 'CheckRow',
  props: { item: { type: Object, required: true }, busy: Boolean },
  emits: ['answer'],
  setup(props, { emit }) {
    const pending = ref(null)          // ответ, которому нужно время или комментарий
    const value = ref('')
    const bad = ref(false)
    const submit = () => {
      const a = pending.value
      if (a.needs_time) {
        const t = normalizeTime(value.value)
        bad.value = !t
        if (t) emit('answer', props.item, a.key, t, '')
        return
      }
      const text = value.value.trim()
      bad.value = !text
      if (text) emit('answer', props.item, a.key, null, text)
    }
    const choose = (a) => {
      if (a.needs_time || a.needs_comment) {
        pending.value = a
        value.value = ''
        bad.value = false
      } else {
        emit('answer', props.item, a.key, null, '')
      }
    }
    return () => {
      const it = props.item
      const morning = isCall(it)
      const answers = it.answers && it.answers.length ? it.answers : FALLBACK_ANSWERS
      const where = it.operation === 'loading' ? 'погрузка' : it.operation === 'unloading' ? 'выгрузка' : ''
      const meta = [it.title, where, 'срок ' + it.due_text, morning ? '' : leftText(it.minutes_left)].filter(Boolean).join(' · ')
      const stage = !morning && STAGE_TEXT[it.stage] ? h('span', { class: ['cg__stage', 'cg__stage--' + it.stage] }, STAGE_TEXT[it.stage]) : null
      const a = pending.value
      const actions = a
        ? [
            h('input', {
              class: ['cg__time', { 'cg__note': a.needs_comment, 'is-bad': bad.value }], value: value.value,
              placeholder: a.needs_time ? 'ЧЧ:ММ' : 'На сколько и почему опоздали',
              inputmode: a.needs_time ? 'numeric' : 'text',
              'aria-label': a.needs_time ? 'Во сколько уехала, по времени площадки' : 'Комментарий',
              onInput: (e) => { value.value = e.target.value; bad.value = false },
              onKeydown: (e) => { if (e.key === 'Enter') submit() },
            }),
            h('button', { class: 'cg__btn cg__btn--ok', type: 'button', disabled: props.busy, onClick: submit }, 'Сохранить'),
            h('button', { class: 'cg__btn cg__btn--ghost', type: 'button', onClick: () => { pending.value = null } }, 'Отмена'),
          ]
        : answers.map((ans) => h('button', {
            class: ['cg__btn', 'cg__btn--' + (TONE[ans.key] || 'ok')], type: 'button', disabled: props.busy,
            onClick: () => choose(ans),
          }, ans.label))
      const hint = a ? (a.hint || (a.needs_time ? 'Время — по часам площадки' : '')) : ''
      return h('div', { class: ['cg__row', { 'is-urgent': it.blocking || it.would_block || it.stage >= 4 }] }, [
        h('div', { class: 'cg__info' }, [
          h('a', { class: 'cg__trip', href: it.cargo_trip_id ? '/trips/' + it.cargo_trip_id : undefined }, 'Рейс ' + it.trip_id),
          h('span', { class: 'cg__site' }, it.site || ''),
          h('span', { class: 'cg__meta' }, meta),
          stage,
          hint ? h('span', { class: 'cg__hint' }, hint) : null,
        ]),
        h('div', { class: 'cg__actions' }, actions),
      ])
    }
  },
})
</script>

<style scoped>
.cg { --ink: #1f2430; --muted: #6b7280; --line: #e7e5df; --red: #dc2626; --amber: #d97706; --gold: #f2b705;
  position: fixed; right: 20px; bottom: 20px; z-index: 3000; width: min(560px, calc(100vw - 32px)); }
.cg--block { inset: 0; width: auto; display: grid; place-items: center; padding: 16px; }
.cg__veil { position: absolute; inset: 0; background: rgba(17, 20, 28, .62); backdrop-filter: blur(3px); }
.cg__panel { position: relative; max-height: calc(100vh - 40px); overflow: auto; background: #fff; border-radius: 22px;
  border: 3px solid var(--amber); box-shadow: 0 30px 70px -30px rgba(17, 20, 28, .7); padding: 18px 20px;
  animation: cg-in .35s cubic-bezier(.2, .7, .2, 1); }
.cg--block .cg__panel { width: min(760px, 100%); border-color: var(--red); animation: cg-in .35s, cg-pulse 2.4s ease-in-out infinite; }
.cg__head { display: flex; gap: 14px; align-items: flex-start; }
.cg__siren { font-size: 34px; line-height: 1; }
.cg__titles { flex: 1; min-width: 0; }
.cg__title { margin: 0; font-size: 22px; line-height: 1.2; font-weight: 800; letter-spacing: .01em; color: var(--ink); text-transform: uppercase; }
.cg--block .cg__title { color: var(--red); font-size: 26px; }
.cg__lead { margin: 6px 0 0; font-size: 14px; color: var(--muted); }
.cg__fold { border: 1px solid var(--line); background: #f7f5ef; border-radius: 999px; padding: 6px 12px; font-size: 13px; cursor: pointer; }
.cg--folded .cg__lead { display: none; }
.cg__body { margin-top: 14px; display: flex; flex-direction: column; gap: 14px; }
.cg__group-title { margin: 0 0 8px; font-size: 15px; font-weight: 700; color: var(--ink); }
.cg__row { display: flex; flex-wrap: wrap; gap: 10px 14px; align-items: center; justify-content: space-between;
  padding: 12px 14px; border-radius: 14px; background: #fbfaf7; border: 1px solid var(--line); border-left: 5px solid var(--amber); }
.cg__row + .cg__row { margin-top: 8px; }
.cg__row.is-urgent { border-left-color: var(--red); background: #fff6f6; }
.cg__info { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1 1 220px; }
.cg__trip { font-weight: 800; font-size: 16px; color: var(--ink); text-decoration: none; }
.cg__trip:hover { text-decoration: underline; }
.cg__site { font-size: 13.5px; color: #374151; }
.cg__meta { font-size: 12.5px; color: var(--muted); }
.cg__stage { align-self: flex-start; margin-top: 4px; font-size: 12px; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: #fff3d6; color: #92400e; }
.cg__stage--2, .cg__stage--3, .cg__stage--4 { background: #fee2e2; color: #991b1b; }
.cg__hint { font-size: 12px; color: var(--muted); }
.cg__actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.cg__btn { border: 0; border-radius: 999px; padding: 9px 14px; font-size: 14px; font-weight: 700; cursor: pointer; transition: transform .15s; }
.cg__btn:hover:not(:disabled) { transform: translateY(-1px); }
.cg__btn:disabled { opacity: .55; cursor: progress; }
.cg__btn--stand { background: var(--red); color: #fff; }
.cg__btn--ok { background: linear-gradient(135deg, #ffe08a, var(--gold)); color: var(--ink); }
.cg__btn--ghost { background: #f3f1ea; color: var(--ink); }
.cg__btn--warn { background: #fde68a; color: #78350f; }
.cg__note { width: 260px; text-align: left; font-weight: 500; font-size: 14px; }
.cg__warn { margin: 0; padding: 10px 12px; border-radius: 12px; background: #fff7e6; border: 1px solid #fcd34d; color: #78350f; font-size: 13.5px; font-weight: 600; }
.cg__time { width: 84px; padding: 8px 10px; border-radius: 10px; border: 1px solid var(--line); font-size: 15px; font-weight: 700; text-align: center; }
.cg__time.is-bad { border-color: var(--red); background: #fff6f6; }
.cg__error { margin: 0; color: var(--red); font-size: 13.5px; }
.cg__test { margin: 0; font-size: 12.5px; color: var(--muted); }
@keyframes cg-in { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes cg-pulse { 0%, 100% { box-shadow: 0 30px 70px -30px rgba(220, 38, 38, .6); } 50% { box-shadow: 0 0 0 8px rgba(220, 38, 38, .18); } }
@media (prefers-reduced-motion: reduce) { .cg__panel { animation: none !important; } }
@media (max-width: 520px) { .cg { right: 8px; bottom: 8px; } .cg__title { font-size: 18px; } }
</style>
